import type React from "react";
import {useCallback, useMemo} from "react";
import {useNavigate} from "react-router";
import {Box, Typography} from "@mui/material";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid";
import {useDataGridQueryState} from "~/components/data-grid/useDataGridQueryState";
import {useStores} from "~/stores";
import {useComplianceRequirementsGridQuery} from "~/pages/supplier-360/tabs/SupplierCompliance/hooks/useComplianceRequirementsGridQuery";
import {
    type ComplianceRequirementRow,
    getComplianceRequirementColumns,
} from "~/pages/supplier-360/tabs/SupplierCompliance/components/complianceTable/complianceRequirementColumns";
import {DRAWER_HEADER_HEIGHT_LG} from "~/components/main/drawer/DrawerConstants.ts";

type ComplianceRecordsTableProps = {
    entityId: number;
    onEdit: (row: ComplianceRequirementRow) => void;
};

/**
 * The "Compliance records" lens: `compliance_requirement/muix/grid/` (§2.8a) paged, filtered and
 * sorted server-side. The Layer, Applies to and Qualification columns read the BU fields the
 * endpoint expands through `related_data` — no client-side join.
 */
export const ComplianceRecordsTable: React.FC<ComplianceRecordsTableProps> = ({
                                                                                  entityId,
                                                                                  onEdit,
                                                                              }) => {
    const {bagStore} = useStores();
    const gridState = useDataGridQueryState({resetPageOn: entityId});

    const {data: requirementsResp, isFetching, isError} = useComplianceRequirementsGridQuery({
        entityId,
        databagId: bagStore.bagId ?? undefined,
        page: gridState.page,
        pageSize: gridState.pageSize,
        sortModel: gridState.sortModel,
        filterModel: gridState.filterModel,
    });

    const rowCount = requirementsResp?.rowCount ?? 0;

    const rows = requirementsResp?.rows ?? [];

    const navigate = useNavigate();
    // The record detail page is the `compliance/:recordId` child route.
    const openRecord = useCallback((row: ComplianceRequirementRow) => navigate(String(row.id)), [navigate]);
    const columns = useMemo(() => getComplianceRequirementColumns({onEdit, onOpen: openRecord}), [onEdit, openRecord]);

    if (isError) {
        return (
            <Typography color="text.secondary" sx={{mt: 2}}>
                Could not load compliance records.
            </Typography>
        );
    }

    return (
        <Box sx={{height: `calc(100vh - ${DRAWER_HEADER_HEIGHT_LG}px - 64px - 68px - 56px)`}}>
            <CustomDataGrid
                layoutKey="supplier-360-compliance-records"
                rows={rows}
                columns={columns}
                loading={isFetching}
                rowCount={rowCount}
                {...gridState.dataGridProps}
                // Header filters run server-side, so don't fire one request per keystroke.
                filterDebounceMs={500}
                showToolbar
                disableRowSelectionOnClick
                // The whole row opens the record detail, same as its "Details" button.
                onRowClick={({row}) => openRecord(row as ComplianceRequirementRow)}
                sx={{'& .MuiDataGrid-row': {cursor: 'pointer'}}}
                toolbarProps={{title: `${rowCount} Compliance records`}}
                getRowHeight={() => 72}
                localeText={{
                    noRowsLabel: gridState.hasActiveFilters
                        ? 'No record matches these filters.'
                        : 'No compliance records.',
                }}
            />
        </Box>
    );
};
